import React, { useState } from 'react';
import { Phone, X, MessageCircle, Mail, MessageSquare } from 'lucide-react';
import { useSettings } from '../../contexts/SettingsContext';

export function FloatingCallButton() {
  const [expanded, setExpanded] = useState(false);
  const { settings } = useSettings();
  
  // Use settings from context with fallback defaults
  const phoneNumber = settings.phone_number || '+1 (787) 555-0123';
  const whatsappNumber = settings.whatsapp_number || '17875550123';
  const email = settings.email || 'tourfinder.puertorico@gmail.com';

  // Format phone number for tel: link (remove spaces and parentheses)
  const getPhoneLink = () => {
    return phoneNumber.replace(/[\s()-]/g, '');
  };

  // Get WhatsApp link - use whatsapp_number which should be digits only
  const getWhatsAppLink = () => {
    // Remove all non-digit characters for WhatsApp
    const cleanNumber = whatsappNumber.replace(/\D/g, '');
    return `https://wa.me/${cleanNumber}?text=Hi! I have a question about your tours.`;
  };

  // Get SMS link - opens native SMS app
  const getSMSLink = () => {
    const phone = getPhoneLink();
    return `sms:${phone}?body=Hi! I have a question about your tours.`;
  };

  // Get Email link - opens native email app (Gmail, Mail, etc.)
  const getEmailLink = () => {
    return `mailto:${email}?subject=Tour Inquiry&body=Hi! I have a question about your tours.`;
  };

  return (
    <div className="fixed bottom-28 right-4 z-40 flex flex-col items-end gap-3" data-testid="floating-call-btn">
      {/* Expanded Options */}
      {expanded && (
        <div className="flex flex-col gap-2 animate-in slide-in-from-bottom-2 duration-200">
          {/* Email Option - Opens device email app */}
          <a
            href={getEmailLink()}
            className="flex items-center gap-2 bg-blue-500 text-white px-4 py-3 rounded-full shadow-lg hover:bg-blue-600 transition-colors"
            data-testid="email-btn"
          >
            <Mail className="h-5 w-5" />
            <span className="font-medium">Email</span>
          </a>
          
          {/* SMS Option - Opens device SMS app */}
          <a
            href={getSMSLink()}
            className="flex items-center gap-2 bg-purple-500 text-white px-4 py-3 rounded-full shadow-lg hover:bg-purple-600 transition-colors"
            data-testid="sms-btn"
          >
            <MessageSquare className="h-5 w-5" />
            <span className="font-medium">Text</span>
          </a>
          
          {/* WhatsApp Option */}
          <a
            href={getWhatsAppLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-green-500 text-white px-4 py-3 rounded-full shadow-lg hover:bg-green-600 transition-colors"
            data-testid="whatsapp-btn"
          >
            <MessageCircle className="h-5 w-5" />
            <span className="font-medium">WhatsApp</span>
          </a>
          
          {/* Call Option - Using anchor tag for better mobile support */}
          <a
            href={`tel:${getPhoneLink()}`}
            className="flex items-center gap-2 bg-primary text-white px-4 py-3 rounded-full shadow-lg hover:bg-primary/90 transition-colors"
            data-testid="call-btn"
          >
            <Phone className="h-5 w-5" />
            <span className="font-medium">Call</span>
          </a>
        </div>
      )}

      {/* Main Button */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={`flex items-center gap-2 px-5 py-4 rounded-full shadow-xl transition-all duration-200 ${
          expanded 
            ? 'bg-gray-700 text-white' 
            : 'bg-secondary text-secondary-foreground hover:bg-secondary/90'
        }`}
        data-testid="call-agent-toggle"
      >
        {expanded ? (
          <>
            <X className="h-5 w-5" />
            <span className="font-semibold">Close</span>
          </>
        ) : (
          <>
            <Phone className="h-5 w-5" />
            <span className="font-semibold">Contact</span>
          </>
        )}
      </button>
    </div>
  );
}
